/**
 * Single/Bulk schedule selector for Step 2
 */

import { motion } from "framer-motion";
import { Layers, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";

export function ScheduleTypeSelector({
  scheduleType,
  setScheduleType,
  outputType,
  inputType,
  days,
  setDays,
  postsPerDay,
  setPostsPerDay,
  bulkVideoMode,
  setBulkVideoMode,
  totalCreditsNeeded,
  totalVideoCreditsNeeded,
  hasEnoughPostCredits,
  hasEnoughVideoCredits,
  postsRemaining,
  videosRemaining
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-base p-6"
    >
      <label className="text-sm text-viraly-muted mb-3 block">
        How many {outputType === "video" ? "videos" : "posts"}?
      </label>
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setScheduleType("single")}
          data-testid="schedule-single"
          className={`p-4 rounded-xl border-2 transition-all ${
            scheduleType === "single"
              ? "border-viraly-orange bg-viraly-orange/10"
              : "border-white/10 hover:border-white/20"
          }`}
        >
          <Layers className="w-5 h-5 mb-2" />
          <p className="font-medium">Single Post</p>
          <p className="text-xs text-viraly-muted">Create one post now</p>
        </button>
        <button
          onClick={() => setScheduleType("bulk")}
          data-testid="schedule-bulk"
          className={`p-4 rounded-xl border-2 transition-all ${
            scheduleType === "bulk"
              ? "border-viraly-orange bg-viraly-orange/10"
              : "border-white/10 hover:border-white/20"
          }`}
        >
          <Calendar className="w-5 h-5 mb-2" />
          <p className="font-medium">Bulk Schedule</p>
          <p className="text-xs text-viraly-muted">Create multiple days</p>
        </button>
      </div>
      
      {scheduleType === "bulk" && (
        <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-viraly-muted mb-1 block">Days</label>
            <Input
              type="number"
              min="1"
              max="30"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value) || 1)}
              className="input-base"
              data-testid="bulk-days-input"
            />
          </div>
          <div>
            <label className="text-xs text-viraly-muted mb-1 block">
              {outputType === "video" ? "Videos" : "Posts"}/Day
            </label>
            <Input
              type="number"
              min="1"
              max="5"
              value={postsPerDay}
              onChange={(e) => setPostsPerDay(parseInt(e.target.value) || 1)}
              className="input-base"
              data-testid="bulk-posts-per-day-input"
            />
          </div>
          
          {/* Bulk info messages */}
          <div className="col-span-2 p-3 rounded-lg bg-viraly-orange/10 border border-viraly-orange/30">
            {inputType === "video" && outputType === "post" && (
              <p className="text-sm text-viraly-light">
                Each day gets <span className="font-semibold text-viraly-orange">unique captions & hashtags</span>.
                <span className="text-xs text-viraly-muted block mt-1">
                  Your video stays the same, we create different social posts for variety.
                </span>
              </p>
            )}
            
            {inputType === "image" && outputType === "video" && (
              <div className="space-y-3">
                <p className="text-sm text-viraly-light font-medium">How should we create your bulk videos?</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setBulkVideoMode("same")}
                    className={`p-2 rounded-lg text-left transition-all ${
                      bulkVideoMode === "same"
                        ? "bg-viraly-orange/20 border-viraly-orange border"
                        : "bg-white/5 border-white/10 border hover:bg-white/10"
                    }`}
                  >
                    <p className="text-xs font-medium">Same Video, Different Posts</p>
                    <p className="text-[10px] text-viraly-muted">Animate your image once, unique captions daily</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBulkVideoMode("inspired")}
                    className={`p-2 rounded-lg text-left transition-all ${
                      bulkVideoMode === "inspired"
                        ? "bg-viraly-orange/20 border-viraly-orange border"
                        : "bg-white/5 border-white/10 border hover:bg-white/10"
                    }`}
                  >
                    <p className="text-xs font-medium">AI-Inspired Variations</p>
                    <p className="text-[10px] text-viraly-muted">AI creates unique videos inspired by your image</p>
                  </button>
                </div>
              </div>
            )}
            
            {(inputType === "url" || inputType === "text") && outputType === "video" && (
              <p className="text-sm text-viraly-light">
                Each day gets a <span className="font-semibold text-viraly-orange">unique AI-generated video</span>.
                <span className="text-xs text-viraly-muted block mt-1">
                  AI creates fresh video content for each scheduled day.
                </span>
              </p>
            )}
            
            {outputType === "post" && inputType !== "video" && (
              <p className="text-sm text-viraly-light">
                Each day gets a <span className="font-semibold text-viraly-orange">unique AI-generated image</span> with unique captions.
              </p>
            )}
          </div>
          
          {/* Credits Summary */}
          <p className="col-span-2 text-xs text-viraly-muted">
            {outputType === "video" ? (
              inputType === "video" ? (
                <>Total: <span className="font-semibold text-white">{totalCreditsNeeded} post credit{totalCreditsNeeded > 1 ? 's' : ''}</span> for {days} day{days > 1 ? 's' : ''} ({hasEnoughPostCredits ? 
                  <span className="text-viraly-success">You have enough</span> : 
                  <span className="text-red-400">Need {totalCreditsNeeded - postsRemaining} more</span>
                })</>
              ) : (
                <>Total: <span className="font-semibold text-white">{totalVideoCreditsNeeded} video credit{totalVideoCreditsNeeded > 1 ? 's' : ''}</span> for {days} day{days > 1 ? 's' : ''} ({hasEnoughVideoCredits ? 
                  <span className="text-viraly-success">You have enough</span> : 
                  <span className="text-red-400">Need {totalVideoCreditsNeeded - videosRemaining} more</span>
                })</>
              )
            ) : (
              <>Total: <span className="font-semibold text-white">{totalCreditsNeeded} post credit{totalCreditsNeeded > 1 ? 's' : ''}</span> for {days} day{days > 1 ? 's' : ''} ({hasEnoughPostCredits ? 
                <span className="text-viraly-success">You have enough</span> : 
                <span className="text-red-400">Need {totalCreditsNeeded - postsRemaining} more</span>
              })</>
            )}
          </p>
        </div>
      )}
    </motion.div>
  );
}
